import { useState, useEffect, useRef, useMemo } from "react";
import { Plus } from "lucide-react";
import { useTran, useFormDirty } from "@/hooks";
import AppDialog from "@/components/common/AppDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import EntryRowTable from "../entries/EntryRowTable";
import { observer } from "mobx-react-lite";
import {
  useDisclosureStore,
  useEntryUIStore,
  useValidationStore,
} from "../../hooks/useDataEntryStore";
import { saveSubEntries } from "../../operations/entry.ops";
import { evaluateFormula } from "@/features/esg/utils/formula-evaluator";
import { showToast } from "@/lib/utils/toast";
import { reportingYearStore } from "@/features/reporting-boundary/stores/reporting-year.store";

const toEditable = (entry) => ({
  id: entry._id,
  _id: entry._id,
  label: entry.label || "",
  reportingMonth: entry.reportingDate || "",
  entryDate: entry.entryDate || "",
  value: entry.value ?? "",
  unit: entry.unit || "",
  year: entry.year ? String(entry.year) : "",
  location: entry.location || "",
  category: entry.category || "",
  denominator: entry.denominator || "",
  remark: entry.remark || "",
});

const newEntry = () => ({
  id: crypto.randomUUID(),
  label: "",
  reportingMonth: "",
  entryDate: "",
  value: "",
  unit: "",
  year: "",
  location: "",
  category: "",
  denominator: "",
});

const buildSectionsFromEntries = (groupedEntries, subEntryDefs) =>
  subEntryDefs.map((def, i) => {
    const group = groupedEntries[i];
    const entries = group?.entries?.length ? group.entries.map(toEditable) : [];
    return {
      index: i,
      title: def.title || `Sub Entry ${i + 1}`,
      entries,
    };
  });

const sumSectionValues = (entries) => {
  let total = 0;
  let hasValue = false;
  for (const e of entries) {
    const num = parseFloat(e.value);
    if (Number.isFinite(num)) {
      total += num;
      hasValue = true;
    }
  }
  return hasValue ? total : null;
};

const EditSubEntriesDialog = observer(() => {
  const { t } = useTran();
  const { activeDisclosure } = useDisclosureStore();
  const { editingSubEntries, updatingEntry, setEditingSubEntries } =
    useEntryUIStore();
  const { errors, validateSubEntrySections, clearErrors } =
    useValidationStore();

  const [selectedReportingYear, setSelectedReportingYear] = useState(null);

  const { reportingYears } = reportingYearStore;

  const getYearsInReportingYear = (reportingYear = {}) => {
    const startYear = Number(reportingYear.startYear);
    const endYear = Number(reportingYear.endYear ?? reportingYear.startYear);
    if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) return [];

    const years = [];
    for (let year = startYear; year <= endYear; year += 1) {
      years.push(year);
    }
    return years;
  };

  useEffect(() => {
    if (!editingSubEntries?.year || !reportingYears?.length) {
      return;
    }

    const selectedYear = Number(String(editingSubEntries?.year));

    const selectedReporting = reportingYears.find((reportingYear) =>
      getYearsInReportingYear(reportingYear).includes(Number(selectedYear)),
    );

    setSelectedReportingYear(selectedReporting || null);
  }, [editingSubEntries, reportingYears]);

  const {
    _id: disclosureId,
    code,
    title,
    formula,
    subEntries: subEntryDefs,
  } = activeDisclosure || {};

  const [sections, setSections] = useState([]);
  const originalIdsRef = useRef(new Set());

  const open = !!editingSubEntries;
  const formValues = useMemo(() => ({ sections }), [sections]);
  const isDirty = useFormDirty(formValues, open);

  const sectionTotals = useMemo(
    () => sections.map((sec) => sumSectionValues(sec.entries)),
    [sections],
  );

  const formulaTotal = useMemo(() => {
    if (!formula || !subEntryDefs?.length) return null;
    const valueMap = {};
    subEntryDefs.forEach((def, i) => {
      const key = def.formulaKey || `SE${i + 1}`;
      const total = sectionTotals[i];
      if (total != null) valueMap[key] = total;
    });
    return evaluateFormula(formula, valueMap);
  }, [formula, subEntryDefs, sectionTotals]);

  const formulaError = useMemo(() => {
    if (formulaTotal == null) return null;
    if (activeDisclosure?.defaultUnit !== "%") return null;
    if (formulaTotal < 0 || formulaTotal > 100)
      return "Percentage must be between 0% and 100%";
    return null;
  }, [formulaTotal, activeDisclosure?.defaultUnit]);

  const plainTotal = useMemo(() => {
    if (formula) return null;
    const sum = sectionTotals.reduce((a, v) => a + (v ?? 0), 0);
    return sectionTotals.some((v) => v != null) ? sum : null;
  }, [formula, sectionTotals]);

  useEffect(() => {
    if (editingSubEntries && subEntryDefs) {
      const built = buildSectionsFromEntries(
        editingSubEntries.groupedEntries,
        subEntryDefs,
      );
      setSections(built);

      const ids = new Set();
      for (const sec of built) {
        for (const e of sec.entries) {
          if (e._id) ids.add(e._id);
        }
      }
      originalIdsRef.current = ids;
    }
  }, [editingSubEntries, subEntryDefs]);

  const updateEntry = (sectionIndex, entryId, field, value) =>
    setSections((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex
          ? {
              ...sec,
              entries: sec.entries.map((e) =>
                e.id === entryId ? { ...e, [field]: value } : e,
              ),
            }
          : sec,
      ),
    );

  const addEntry = (sectionIndex) =>
    setSections((prev) =>
      prev.map((sec, i) =>
        i === sectionIndex
          ? { ...sec, entries: [...sec.entries, newEntry()] }
          : sec,
      ),
    );

  const removeEntry = (sectionIndex, entryId) =>
    setSections((prev) => {
      const totalEntries = prev.reduce(
        (sum, sec) => sum + sec.entries.length,
        0,
      );
      if (totalEntries <= 1) return prev;
      return prev.map((sec, i) =>
        i === sectionIndex
          ? { ...sec, entries: sec.entries.filter((e) => e.id !== entryId) }
          : sec,
      );
    });

  const doClose = () => {
    setEditingSubEntries(null);
    clearErrors();
  };

  const handleClose = () => {
    doClose();
  };

  const handleSave = async () => {
    if (sections.some((section) => section.entries?.length === 0)) {
      showToast.error("Please fill at lease 1 data at each sub-entry");
      return;
    }
    if (
      !validateSubEntrySections(sections, subEntryDefs || [], {
        validations: activeDisclosure?.validations,
      })
    )
      return;

    const { orgId, year } = editingSubEntries;

    const toUpdate = [];
    const toCreate = [];
    const currentIds = new Set();

    for (const section of sections) {
      for (const entry of section.entries) {
        const { id: _entryId, _id, reportingMonth, ...fields } = entry;
        const reportingDate = reportingMonth || (year ? `${year}-01-01` : "");

        const hasValue =
          fields.value !== undefined &&
          fields.value !== null &&
          fields.value !== "";
        const hasRemark =
          fields.remark !== undefined &&
          fields.remark !== null &&
          String(fields.remark).trim() !== "";

        let finalUnit = fields.unit;

        if (!hasValue && hasRemark) {
          finalUnit = null;
        }

        if (hasValue && (finalUnit === undefined || finalUnit === "")) {
          finalUnit = fields.unit;
        }

        // Combine fields with the corrected unit
        const sanitizedFields = {
          ...fields,
          unit: finalUnit,
        };

        if (_id) {
          currentIds.add(_id);
          toUpdate.push({ _id, ...sanitizedFields, reportingDate });
        } else {
          toCreate.push({
            ...fields,
            reportingDate,
            orgId,
            disclosureId,
            year,
            subEntryGroupIndex: section.index,
          });
        }
      }
    }

    const toDelete = [];
    for (const origId of originalIdsRef.current) {
      if (!currentIds.has(origId)) toDelete.push(origId);
    }

    if (!toUpdate.length && !toCreate.length && !toDelete.length) {
      doClose();
      return;
    }

    const success = await saveSubEntries({ toUpdate, toCreate, toDelete });
    if (success) doClose();
  };

  return (
    <AppDialog
      open={!!editingSubEntries}
      onOpenChange={(v) => !v && handleClose()}
      isDirty={isDirty}
      header={
        <div className="flex items-center gap-2">
          {code && <Badge>{code}</Badge>}
          <span className="text-lg font-semibold">{title}</span>
        </div>
      }
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={handleClose}>
            {t("Cancel")}
          </Button>
          <Button
            disabled={updatingEntry || !!formulaError}
            onClick={handleSave}
          >
            {t("Save Changes")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Total banner */}
        {formula ? (
          <div
            className={`flex items-center justify-between rounded-lg px-4 py-2.5 ${formulaError ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}
          >
            <span className="text-sm font-medium">
              {formulaError || `${t("Total")} ${formula}`}
            </span>
            <span className="text-sm font-semibold">
              {formulaTotal != null
                ? `${formulaTotal}${activeDisclosure?.defaultUnit || ""}`
                : "N/A"}
            </span>
          </div>
        ) : plainTotal != null ? (
          <div className="flex items-center justify-between rounded-lg bg-primary px-4 py-2.5 text-primary-foreground">
            <span className="text-sm font-medium">{t("Total")}</span>
            <span className="text-sm font-semibold">{plainTotal}</span>
          </div>
        ) : null}

        {/* Sub-entry sections */}
        {sections.map((section, sIdx) => (
          <div
            key={section.index}
            className="rounded-lg border border-border overflow-hidden"
          >
            {/* Section header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <Badge variant="outline">SE{section.index + 1}</Badge>
                <span className="text-sm font-semibold">{section.title}</span>
                {sectionTotals[sIdx] != null && (
                  <span className="text-sm font-semibold text-primary">
                    {sectionTotals[sIdx]}
                  </span>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addEntry(section.index)}
              >
                <Plus size={14} />
                {t("Add Entry")}
              </Button>
            </div>

            {/* Entries table */}
            <EntryRowTable
              entries={section.entries}
              inputType={subEntryDefs?.[section.index]?.inputType || "value"}
              unitOptions={subEntryDefs?.[section.index]?.unitOptions || []}
              errors={errors}
              onUpdate={(entryId, field, value) =>
                updateEntry(section.index, entryId, field, value)
              }
              onRemove={(entryId) => removeEntry(section.index, entryId)}
              masterDataYear={editingSubEntries?.year}
              selectedReportingYear={selectedReportingYear}
              isEdit={true}
            />
          </div>
        ))}
      </div>
    </AppDialog>
  );
});

export default EditSubEntriesDialog;
